import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyIcon, ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const PasswordSection = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  const [errors, setErrors] = useState({});
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle form submission
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setPasswordSubmitting(true);
    setSuccessMessage('');
    
    // Validation
    const newErrors = {};
    if (!formData.currentPassword) newErrors.currentPassword = t('settings.password.validation.currentRequired');
    if (!formData.newPassword) newErrors.newPassword = t('settings.password.validation.newRequired');
    else if (formData.newPassword.length < 8) newErrors.newPassword = t('settings.password.validation.minLength');
    if (!formData.confirmPassword) newErrors.confirmPassword = t('settings.password.validation.confirmRequired');
    else if (formData.newPassword !== formData.confirmPassword) newErrors.confirmPassword = t('settings.password.validation.noMatch');
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setPasswordSubmitting(false);
      return;
    }

    // In a real application, you would make an API call here
    setTimeout(() => {
      console.log("Password updated");
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setSuccessMessage(t('settings.password.successMessage'));
      setPasswordSubmitting(false);
    }, 1000);
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
      {/* Header */}
      <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <KeyIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              {t('settings.password.title')}
            </h3>
          </div>
        </div>
      </div>
      
      <div className="p-5">
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label htmlFor="currentPassword" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('settings.password.fields.current')}
            </label>
            <input 
              type="password" 
              id="currentPassword" 
              name="currentPassword" 
              value={formData.currentPassword} 
              onChange={handleChange} 
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-slate-700 dark:text-white sm:text-sm" 
              placeholder={t('settings.password.placeholders.current')}
            />
            {errors.currentPassword && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {errors.currentPassword}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('settings.password.fields.new')}
            </label>
            <input 
              type="password" 
              id="newPassword" 
              name="newPassword" 
              value={formData.newPassword} 
              onChange={handleChange} 
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-slate-700 dark:text-white sm:text-sm" 
              placeholder={t('settings.password.placeholders.new')}
            />
            {errors.newPassword && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {errors.newPassword}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('settings.password.fields.confirm')}
            </label>
            <input 
              type="password" 
              id="confirmPassword" 
              name="confirmPassword" 
              value={formData.confirmPassword} 
              onChange={handleChange} 
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-slate-700 dark:text-white sm:text-sm" 
              placeholder={t('settings.password.placeholders.confirm')}
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {errors.confirmPassword}
              </p>
            )}
          </div>

          {successMessage && (
            <div className="p-2 rounded-md bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-sm flex items-center">
              <CheckCircleIcon className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <button 
              type="submit" 
              disabled={passwordSubmitting}
              className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:focus:ring-offset-gray-800 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
            >
              {passwordSubmitting ? t('settings.password.changing') : t('settings.password.changeButton')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordSection;
